import type { ComponentType, SVGProps } from 'react';
import {
  Activity,
  Bell,
  Boxes,
  Brain,
  Briefcase,
  Database,
  Dog,
  FileText,
  Gauge,
  Globe,
  IdCard,
  Inbox,
  Languages,
  LayoutDashboard,
  MessageSquare,
  PawPrint,
  ScrollText,
  Search,
  Settings,
  Share2,
  Smile,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import {
  normalizeAnalysisTab,
  normalizeContentTab,
  type AnalysisTab,
  type ContentTab,
} from '@/lib/route-search';

export type InternalNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** 탭 화면이면 사이드바 항목이 이 tab 파라미터 링크가 된다 */
  search?: { tab: ContentTab | AnalysisTab };
  /** 이 prefix 아래 상세 라우트에서도 활성 처리 (예: 글 → /posts/new) */
  activePrefixes?: string[];
};

export type InternalNavGroup = {
  title: string;
  items: InternalNavItem[];
};

export type ExternalNavItem = {
  href: string;
  label: string;
  icon: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
};

// eslint-disable-next-line react-refresh/only-export-components -- 내비 설정 데이터가 주 export 인 파일의 부속 아이콘
function GithubIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
    </svg>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- 내비 설정 데이터가 주 export 인 파일의 부속 아이콘
function MastodonIcon(props: SVGProps<SVGSVGElement>) {
  // lucide 에 브랜드 아이콘이 없어 GithubIcon 과 같은 방식으로 직접 그린다.
  // 사이드바가 h-4(16px)로 렌더하므로 디테일 대신 실루엣(둥근 방패 + m)만 남긴 형태.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M18.2 8.6v3.9c0 2.6-1.9 4-4.6 4.4-1 .1-2 .1-3 0-2.7-.4-4.8-1.8-4.8-4.4V8.6c0-3.1 2.8-5 6.2-5s6.2 1.9 6.2 5z" />
      <path d="M8.6 13.6V10a1.9 1.9 0 0 1 3.4-1.1 1.9 1.9 0 0 1 3.4 1.1v3.6" />
    </svg>
  );
}

export const INTERNAL_NAV_GROUPS: InternalNavGroup[] = [
  {
    title: '',
    items: [
      { to: '/', label: '대시보드', icon: LayoutDashboard },
    ],
  },
  {
    title: '콘텐츠',
    items: [
      { to: '/content', label: '글', icon: FileText, search: { tab: 'posts' }, activePrefixes: ['/posts'] },
      { to: '/content', label: '포트폴리오', icon: Briefcase, search: { tab: 'portfolio' }, activePrefixes: ['/portfolio'] },
      { to: '/content', label: '이력서', icon: IdCard, search: { tab: 'resume' } },
    ],
  },
  {
    title: '방문자',
    items: [
      { to: '/messages', label: '메시지', icon: Inbox },
      { to: '/chat', label: 'AI 챗', icon: MessageSquare },
      { to: '/memory', label: '기억', icon: Brain },
      { to: '/fediverse', label: '페디버스', icon: Share2 },
    ],
  },
  {
    title: '운영',
    items: [
      { to: '/events', label: '이벤트', icon: Bell },
      { to: '/analysis', label: '트래픽', icon: Gauge, search: { tab: 'traffic' } },
      { to: '/analysis', label: '검색', icon: Search, search: { tab: 'search' } },
      { to: '/analysis', label: 'SEO', icon: Globe, search: { tab: 'seo' } },
      { to: '/cache', label: '캐시', icon: Database },
      { to: '/trash', label: '휴지통', icon: Trash2 },
      { to: '/settings', label: '설정', icon: Settings },
    ],
  },
];

export const EXTERNAL_NAV_ITEMS: ExternalNavItem[] = [
  { href: 'https://logs.zerry.co.kr', label: 'Dozzle 로그', icon: ScrollText },
  { href: 'https://up.zerry.co.kr', label: 'Uptime Kuma', icon: Activity },
  { href: 'https://qdrant.zerry.co.kr/dashboard', label: 'Qdrant 대시보드', icon: Boxes },
  { href: 'https://joy.zerry.co.kr', label: 'JoyDay', icon: Smile },
  { href: 'https://garu.zerry.co.kr', label: 'Garu 형태소 분석기', icon: Languages },
  { href: 'https://dogfortune.zerry.co.kr', label: 'DogFortune', icon: Dog },
  { href: 'https://dogfortune-admin.zerry.co.kr', label: 'DogFortune 어드민', icon: PawPrint },
  { href: 'https://github.com/ongjin?tab=repositories', label: 'GitHub', icon: GithubIcon },
  // 우리 액터 URL(zerry.co.kr/users/zerry)을 걸면 안 된다 — 연합이 push-only 라 사람이 볼 HTML
  // 프로필이 없어서 브라우저 Accept 로 요청하면 404 다. 마스토돈의 원격 프로필 뷰로 보낸다.
  { href: 'https://mastodon.social/@zerry@zerry.co.kr', label: '마스토돈', icon: MastodonIcon },
];

const HIDDEN_BREADCRUMB_LABELS = [
  ['posts', '글'],
  ['portfolio', '포트폴리오'],
] as const;

const BREADCRUMB_LABELS = new Map<string, string>([
  ...INTERNAL_NAV_GROUPS.flatMap(group =>
    group.items.map(item => [item.to === '/' ? '' : item.to.replace(/^\//, ''), item.label] as const),
  ),
  ...HIDDEN_BREADCRUMB_LABELS,
]);

export function getBreadcrumbLabel(segment: string): string {
  return BREADCRUMB_LABELS.get(segment) ?? segment;
}

/** to 경로의 탭 파라미터 정규화 — route-search 의 기본 탭 규칙을 재사용 */
function normalizeTabFor(to: string, rawTab: string | undefined): string | undefined {
  if (to === '/content') return normalizeContentTab(rawTab);
  if (to === '/analysis') return normalizeAnalysisTab(rawTab);
  return rawTab;
}

export function isNavItemActive(item: InternalNavItem, pathname: string, rawTab: string | undefined): boolean {
  if (item.activePrefixes?.some(p => pathname === p || pathname.startsWith(`${p}/`))) return true;
  if (item.to === '/') return pathname === '/';
  const onPath = pathname === item.to || pathname.startsWith(`${item.to}/`);
  if (!onPath) return false;
  if (!item.search) return true;
  return normalizeTabFor(item.to, rawTab) === item.search.tab;
}

/** TopBar 브레드크럼: 그룹/항목 라벨 + 나머지 경로 조각(tail, 디코딩됨) */
export function getBreadcrumbTrail(pathname: string, tab?: string): { labels: string[]; tail: string[] } {
  const segments = pathname.split('/').filter(Boolean);
  const rootPath = segments.length === 0 ? '/' : `/${segments[0]}`;
  const tail = segments.slice(1).map(decodeURIComponent);

  for (const group of INTERNAL_NAV_GROUPS) {
    const items = group.items.filter(i => i.to === rootPath);
    if (items.length === 0) continue;
    const normalizedTab = normalizeTabFor(rootPath, tab);
    const match = items.find(i => (i.search ? i.search.tab === normalizedTab : true)) ?? items[0];
    return { labels: group.title ? [group.title, match.label] : [match.label], tail };
  }
  // 네비에 없는 루트 (/posts/new, /portfolio/3 등) — 숨은 라벨 fallback
  return { labels: [getBreadcrumbLabel(segments[0] ?? '')], tail };
}
